import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal, Overline } from "@/components/motion";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-4 text-base outline-none transition-colors duration-300 focus:border-accent placeholder:text-muted-foreground/60";

  return (
    <div className="pt-16" data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-16 sm:pt-24 pb-24 sm:pb-32">
        <Reveal>
          <Overline>Contact</Overline>
          <h1 className="mt-4 font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] max-w-3xl">
            Let's talk about what's <span className="italic text-accent">next</span>
          </h1>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-7" delay={0.1}>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={6}
                  maxLength={4000}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell me about the role or project…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-10 py-5 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:bg-foreground hover:text-background disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send message"}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.2}>
            <div className="border border-border p-8 sm:p-10 bg-muted/40 space-y-8">
              {settings.availability && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">Availability</p>
                  <p className="mt-3 flex items-center gap-3 text-base" data-testid="availability-line">
                    <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                    {settings.availability}
                  </p>
                </div>
              )}
              {settings.public_email && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">Email</p>
                  <a
                    href={`mailto:${settings.public_email}`}
                    data-testid="contact-email-link"
                    className="mt-3 inline-block font-serif text-xl sm:text-2xl tracking-tight hover:text-accent transition-colors"
                  >
                    {settings.public_email}
                  </a>
                </div>
              )}
              {settings.linkedin_url && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">LinkedIn</p>
                  <a
                    href={settings.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin-link"
                    className="mt-3 inline-flex items-center gap-2 font-serif text-xl sm:text-2xl tracking-tight hover:text-accent transition-colors"
                  >
                    Connect with me <ArrowUpRight size={18} />
                  </a>
                </div>
              )}
              <div className="pt-6 border-t border-border text-sm text-muted-foreground">
                Based in Delhi / Gurugram, India. Working with teams everywhere.
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
